import type { ApplicationConfiguration } from '@/domains/application/applicationConfiguration.types';
import type { OnboardingCheckpointViewModel } from '@/features/onboarding/types/onboarding.types';
import { getCheckpointVersion } from '@/lib/checkpointKeys';
import type { Checkpoint } from '@/types';

export const hasValidApplicationCheckpointParameters = (
  checkpoint: Checkpoint,
) => {
  if (
    !checkpoint.parameters ||
    !checkpoint.parameters.length ||
    checkpoint.exceptions?.length
  ) {
    return true;
  }

  return checkpoint.parameters.every((param) => param.paramValue !== null);
};

export const getSortedApplicationConfigurations = (
  applicationConfigurations?: ApplicationConfiguration[] | null,
) => {
  if (!applicationConfigurations) {
    return [];
  }

  return applicationConfigurations
    .slice()
    .sort(
      (left, right) =>
        new Date(right.createdAt).getTime() -
        new Date(left.createdAt).getTime(),
    );
};

export const getLatestApplicationConfiguration = (
  sortedConfigurations: ApplicationConfiguration[],
) => {
  return sortedConfigurations[0] ?? null;
};

export const getPreviousApplicationConfiguration = (
  sortedConfigurations: ApplicationConfiguration[],
) => {
  if (sortedConfigurations.length <= 1) {
    return null;
  }

  return sortedConfigurations[1];
};

export const isNewApplicationCheckpoint = (
  checkpoint: Checkpoint,
  previousConfiguration: ApplicationConfiguration | null,
) => {
  if (!previousConfiguration) {
    return true;
  }

  const checkpointVersion = getCheckpointVersion(checkpoint);
  const previousCheckpoint = previousConfiguration.checkpoints.find(
    (currentCheckpoint) =>
      currentCheckpoint.id === checkpoint.id &&
      getCheckpointVersion(currentCheckpoint) === checkpointVersion,
  );

  return !previousCheckpoint;
};

interface FilterApplicationCheckpointsOptions {
  checkpointSearch: string;
  checkpoints: Checkpoint[];
  previousConfiguration: ApplicationConfiguration | null;
}

export const filterApplicationCheckpoints = ({
  checkpointSearch,
  checkpoints,
  previousConfiguration,
}: FilterApplicationCheckpointsOptions) => {
  const normalizedSearchTerms = checkpointSearch
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (!normalizedSearchTerms.length) {
    return checkpoints;
  }

  return checkpoints.filter((checkpoint) => {
    const searchableFields = [
      checkpoint.id,
      checkpoint.description,
      checkpoint.category,
      checkpoint.accountable,
      checkpoint.owner,
      checkpoint.referenceSystem,
      checkpoint.displayName,
      checkpoint.isScored ? 'scored' : 'preview',
      isNewApplicationCheckpoint(checkpoint, previousConfiguration)
        ? 'new'
        : '',
      !hasValidApplicationCheckpointParameters(checkpoint)
        ? 'requires parameters'
        : '',
      checkpoint.exceptions?.length ? 'exception' : '',
      getApplicationCheckpointExceptionApprovalStatus(checkpoint) ===
      'pending'
        ? 'exception pending approval'
        : '',
      getApplicationCheckpointExceptionApprovalStatus(checkpoint) ===
      'approved'
        ? 'exception approved'
        : '',
      getApplicationCheckpointExceptionApprovalStatus(checkpoint) ===
      'rejected'
        ? 'exception rejected'
        : '',
      String(getCheckpointVersion(checkpoint) ?? 'n/a'),
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return normalizedSearchTerms.every((term) =>
      searchableFields.includes(term),
    );
  });
};

export const getCheckpointsCountWithInvalidParameters = (
  latestConfiguration: ApplicationConfiguration | null,
) => {
  if (!latestConfiguration) {
    return 0;
  }

  return latestConfiguration.checkpoints.reduce((count, checkpoint) => {
    if (!checkpoint.parameters?.length) {
      return count;
    }

    return hasValidApplicationCheckpointParameters(checkpoint)
      ? count
      : count + 1;
  }, 0);
};

export const getApplicationCheckpointExceptionApprovalStatus = (
  checkpoint: Checkpoint,
) => {
  if (!checkpoint.exceptions?.length) {
    return null;
  }

  if (checkpoint.exceptions[0].isApproved === null) {
    return 'pending';
  }

  if (checkpoint.exceptions[0].isApproved) {
    return 'approved';
  }

  return 'rejected';
};

export const getApplicationCheckpointVersionLabel = (
  checkpoint: Checkpoint,
) => {
  const version = getCheckpointVersion(checkpoint);

  return version === null || version === undefined ? 'N/A' : `${version}`;
};

export const getApplicationCheckpointRowKey = (checkpoint: Checkpoint) => {
  const version = getCheckpointVersion(checkpoint);
  return `${checkpoint.id}-${version ?? ''}`.replace(/[^a-zA-Z0-9_-]/g, '-');
};

export const mapApplicationCheckpointToOnboardingViewModel = (
  checkpoint: Checkpoint,
  previousConfiguration: ApplicationConfiguration | null,
): OnboardingCheckpointViewModel<Checkpoint> => ({
  accountable: checkpoint.accountable || null,
  category: checkpoint.category || null,
  description: checkpoint.description || '',
  exceptionApprovalStatus:
    getApplicationCheckpointExceptionApprovalStatus(checkpoint),
  id: checkpoint.id,
  isNew: isNewApplicationCheckpoint(checkpoint, previousConfiguration),
  isPreview: !checkpoint.isScored,
  key: getApplicationCheckpointRowKey(checkpoint),
  rawCheckpoint: checkpoint,
  requiresParameters:
    !hasValidApplicationCheckpointParameters(checkpoint),
  versionLabel: getApplicationCheckpointVersionLabel(checkpoint),
});

export const mapApplicationCheckpointsToOnboardingViewModels = (
  checkpoints: Checkpoint[],
  previousConfiguration: ApplicationConfiguration | null,
) =>
  checkpoints.map((checkpoint) =>
    mapApplicationCheckpointToOnboardingViewModel(
      checkpoint,
      previousConfiguration,
    ),
  );
